import React from 'react';
import { motion } from 'framer-motion';
import { Store, Briefcase, Globe, Rocket } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';

export function WhoBenefits() {
  const { t } = useLanguage();

  const benefits = [
    {
      icon: Store,
      key: 'smallBusiness',
      gradient: 'from-blue-500 to-cyan-500'
    },
    {
      icon: Briefcase,
      key: 'localServices',
      gradient: 'from-purple-500 to-pink-500'
    },
    {
      icon: Globe,
      key: 'onlineServices',
      gradient: 'from-orange-500 to-red-500'
    },
    {
      icon: Rocket,
      key: 'startups',
      gradient: 'from-green-500 to-emerald-500'
    }
  ];

  return (
    <section id="offer" className="py-24 md:py-32 bg-slate-950 relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute top-0 left-0 w-96 h-96 bg-primary/5 rounded-full blur-3xl" />
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-purple-500/5 rounded-full blur-3xl" />
      
      <div className="max-w-7xl mx-auto px-6 relative z-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-100px' }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight mb-4">
            <span className="text-white">{t('whoBenefits.title')}</span>
          </h2>
          <p className="text-xl text-slate-300 max-w-2xl mx-auto">
            {t('whoBenefits.subtitle')}
          </p>
        </motion.div>

        {/* Benefits Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
          {benefits.map((benefit, index) => (
            <motion.div
              key={benefit.key}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-50px' }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              className="group relative"
            >
              <div className="relative bg-slate-900/50 backdrop-blur-sm rounded-2xl p-8 border border-slate-800 hover:border-slate-700 transition-all duration-300 hover:transform hover:scale-105">
                {/* Icon */}
                <div className={`w-16 h-16 rounded-xl bg-gradient-to-br ${benefit.gradient} p-0.5 mb-6`}>
                  <div className="w-full h-full bg-slate-950 rounded-xl flex items-center justify-center">
                    <benefit.icon className="w-8 h-8 text-white" />
                  </div>
                </div>

                {/* Content */}
                <h3 className="font-heading text-2xl font-bold text-white mb-3">
                  {t(`whoBenefits.items.${benefit.key}.title`)}
                </h3>
                <p className="text-slate-400 leading-relaxed mb-4">
                  {t(`whoBenefits.items.${benefit.key}.description`)}
                </p>

                {/* Examples */}
                <div className="space-y-2">
                  <p className="text-sm text-slate-500 font-medium">{t('whoBenefits.examples')}:</p>
                  <ul className="space-y-1">
                    {t(`whoBenefits.items.${benefit.key}.examples`).map((example, i) => (
                      <li key={i} className="text-sm text-slate-400 flex items-center gap-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-primary flex-shrink-0" />
                        {example}
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Hover effect */}
                <div className={`absolute inset-0 rounded-2xl bg-gradient-to-br ${benefit.gradient} opacity-0 group-hover:opacity-5 transition-opacity duration-300 pointer-events-none`} />
              </div>
            </motion.div>
          ))}
        </div>

        {/* Bottom note */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.5 }}
          className="mt-16 text-center"
        >
          <p className="text-slate-400 text-lg">
            {t('whoBenefits.note')}
          </p>
        </motion.div>
      </div>
    </section>
  );
}
